import React from 'react';
import { FaBookOpen, FaClipboardList } from 'react-icons/fa';
import { getCourseContent } from '../../utils/progressUtils';

/**
 * ContentModalHeader Component
 * Displays the modal header with course thumbnail background, title, and progress bar
 *
 * @param {Object} props
 * @param {boolean} props.showAchievementView - Whether showing achievement view
 * @param {Function} props.isPathCompletedLocal - Check if path is completed
 * @param {Object} props.modalNavigation - Modal navigation state
 * @param {Object} props.userCurrentCourse - Current course object
 * @param {string} props.currentContentType - Current content type (lesson/submissionForm)
 * @param {Object} props.studentPath - Student's learning path
 * @param {Object} props.userProgress - User progress data
 * @param {Array} props.pathCourses - Array of path courses
 * @param {Function} props.getCourseProgressLocal - Get course progress percentage
 * @param {Function} props.getCompletedContentForCourseLocal - Get completed content count
 * @param {number} props.courseProgress - Course progress percentage
 * @param {Array} props.modalCourseContent - Array of course content
 * @param {Function} props.onClose - Handler to close modal
 */
const ContentModalHeader = ({
  showAchievementView,
  isPathCompletedLocal,
  modalNavigation,
  userCurrentCourse,
  currentContentType,
  studentPath,
  userProgress,
  pathCourses,
  getCourseProgressLocal,
  getCompletedContentForCourseLocal,
  courseProgress,
  modalCourseContent,
  onClose
}) => {
  // Determine current course for background
  const currentCourse = (showAchievementView || isPathCompletedLocal())
    ? null
    : modalNavigation.isViewingBackwards && modalNavigation.viewingCourse
      ? modalNavigation.viewingCourse
      : userCurrentCourse;

  // Calculate total content count excluding reinforcements
  const getTotalContentWithoutReinforcements = (course) => {
    if (!course) return 0;
    const courseContent = getCourseContent(course);
    // Filter out reinforcement items
    return courseContent.filter(content => {
      const isReinforcement = course.reinforcements?.some(r => r._id === content._id);
      return !isReinforcement;
    }).length;
  };

  // Get total for current displayed course
  const totalContentCount = modalNavigation.isViewingBackwards && modalNavigation.viewingCourse
    ? getTotalContentWithoutReinforcements(modalNavigation.viewingCourse)
    : getTotalContentWithoutReinforcements(userCurrentCourse);

  const displayCourseTitle = (showAchievementView || isPathCompletedLocal())
    ? studentPath?.title
    : modalNavigation.isViewingBackwards && modalNavigation.viewingCourse
      ? modalNavigation.viewingCourse.title
      : userCurrentCourse?.title;

  const displayProgress = modalNavigation.isViewingBackwards && modalNavigation.viewingCourse
    ? getCourseProgressLocal(modalNavigation.viewingCourse._id)
    : courseProgress;

  const completedCount = getCompletedContentForCourseLocal(
    modalNavigation.isViewingBackwards && modalNavigation.viewingCourse
      ? modalNavigation.viewingCourse._id
      : userCurrentCourse?._id
  );

  return (
    <>
      {/* Close Button */}
      <button
        onClick={onClose}
        className="fixed top-4 right-4 z-50 w-9 h-9 bg-white hover:bg-muted border border-border rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shadow-soft"
        aria-label="Close modal"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Header */}
      <div className="relative h-32 sm:h-36 flex-shrink-0 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          {currentCourse?.thumbnail?.url || currentCourse?.thumbnail?.data ? (
            <img
              src={currentCourse.thumbnail.url || `data:${currentCourse.thumbnail.contentType};base64,${currentCourse.thumbnail.data}`}
              alt={currentCourse.title}
              className="w-full h-full object-cover blur-sm scale-110"
            />
          ) : (
            <div className="w-full h-full bg-primary" />
          )}
        </div>
        {/* Overlay — lighter than before */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/30" />

        {/* Content */}
        <div className="relative z-10 px-4 sm:px-6 h-full flex flex-col justify-end pb-3 pr-14 sm:pr-16">
          <p className="text-white/70 text-xs font-medium uppercase tracking-wide mb-0.5">
            {(showAchievementView || isPathCompletedLocal()) ? 'Learning Path' : 'Course'}
          </p>
          <h3 className="text-base sm:text-lg font-bold text-white truncate">
            {displayCourseTitle}
          </h3>

          {/* Progress Row */}
          <div className="flex items-center gap-3 mt-2">
            <div className="flex-1 bg-white/25 rounded-full h-1.5">
              <div
                className="h-1.5 rounded-full bg-primary transition-all duration-500"
                style={{ width: `${(showAchievementView || isPathCompletedLocal()) ? 100 : displayProgress}%` }}
              />
            </div>
            <span className="text-white/80 text-xs font-medium tabular-nums flex-shrink-0">
              {(showAchievementView || isPathCompletedLocal())
                ? `${pathCourses.length}/${pathCourses.length} courses`
                : `${completedCount}/${totalContentCount} items`
              }
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContentModalHeader;
